<template>
  <div class="float-card">
    <h3>Station Floats</h3>
    <div v-if="floats.length > 0">
      <details v-for="f in floats" :key="f.name" class="station-item">
        <summary class="station-summary">
          <span class="station-name">{{ f.name }}</span>
          <CurrencyFormatter class="station-amount" :value="f.amount" :currency="companySettings.currency" />
        </summary>
        <table v-if="f.history.length > 0" class="history-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Type</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(entry, i) in f.history" :key="i">
              <td>{{ formatDate(entry.timestamp) }}</td>
              <td>{{ entry.doc_type }}</td>
              <td><CurrencyFormatter :value="entry.value" :currency="companySettings.currency" /></td>
            </tr>
          </tbody>
        </table>
        <p v-else class="no-history">No history available</p>
      </details>
    </div>
    <p v-else class="empty">No stations with float enabled</p>
  </div>
</template>

<script>
import dashify from "dashify";
import { api } from "@monolith/helpers";
import { mapGetters } from "vuex";
import { CurrencyFormatter } from "@monolith/components";

export default {
  name: "SiteFloatCard",
  components: { CurrencyFormatter },
  props: {
    site: { type: String, required: true },
  },
  data() {
    return {
      floats: [],
    };
  },
  computed: {
    ...mapGetters({
      siteMap: "siteMap",
      companyName: "companyName",
      companySettings: "companySettings",
    }),
    stationsForSite() {
      const entry = this.siteMap.find((s) => s.site === this.site);
      return entry.stations.pos || [];
    },
  },
  watch: {
    site() {
      this.loadFloats();
    },
    stationsForSite() {
      this.loadFloats();
    },
  },
  mounted() {
    this.loadFloats();
  },
  methods: {
    formatDate(timestamp) {
      if (!timestamp) return "—";
      const d = new Date(timestamp);
      return d.toLocaleString([], { dateStyle: "short", timeStyle: "short" });
    },
    async loadFloats() {
      const stations = this.stationsForSite.filter((s) => s.float);
      const results = await Promise.all(
        stations.map(async (station) => {
          const slug = dashify(station.name);
          try {
            const [totalRaw, historyRaw] = await Promise.all([
              api.get_station_float(this.companyName, this.site, slug, true),
              api.get_station_float(this.companyName, this.site, slug, false),
            ]);
            const amount = JSON.parse(totalRaw).rows[0].value || 0;
            const history = JSON.parse(historyRaw).rows.map((r) => r.doc).filter(Boolean);
            return { name: station.name, amount, history };
          } catch {
            return { name: station.name, amount: 0, history: [] };
          }
        })
      );
      this.floats = results;
    },
  },
};
</script>

<style scoped lang="scss">
.float-card {
  padding: 1rem;
  height: 100%;
  box-sizing: border-box;

  h3 {
    margin: 0 0 0.75rem;
    font-size: 1rem;
    text-transform: uppercase;
    color: var(--action-colour);
  }

  .station-item {
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    padding: 0.25rem 0;

    .station-summary {
      display: flex;
      justify-content: space-between;
      align-items: center;
      cursor: pointer;
      padding: 0.35rem 0.25rem;
      list-style: none;

      .station-name {
        text-transform: capitalize;
        font-weight: 500;
      }

      .station-amount {
        font-weight: bold;
        color: var(--action-colour);
      }
    }

    .history-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 0.8rem;
      margin: 0.25rem 0 0.5rem 0.75rem;

      th {
        text-align: left;
        padding: 0.2rem 0.25rem;
        opacity: 0.6;
        font-size: 0.72rem;
        text-transform: uppercase;
      }

      td {
        padding: 0.2rem 0.25rem;
      }
    }

    .no-history {
      font-size: 0.8rem;
      opacity: 0.5;
      margin: 0.25rem 0 0.5rem 0.75rem;
    }
  }

  .empty {
    font-size: 0.9rem;
    opacity: 0.6;
  }
}
</style>
